/* eslint-disable default-case */
/* eslint-disable class-methods-use-this */
import jwt_decode from "jwt-decode"
import i18next from "i18next"
import UserApi from "../API/user"
import { User } from "../models/user"
import NavigationService from "./navigation"
import { Service } from "./Service"
import StoreService from "./store"
import UIService from "./UIService"
import { NavigationParam } from "../models/navigationParam"
import { ProviderService } from "./provider"

class UserService extends Service {
    private static instance: UserService
    private navParam: NavigationParam

    constructor() {
        super()
        const navParamService = ProviderService.getInstance().get(
            "NavigationParamService"
        )
        this.navParam = navParamService ? navParamService.getInstance() : null
        // set le nom du service
        this.name = "UserService"
        // ajoute toute les methodes du service au tableau de mapping
        // this.serviceMap = []
        this.serviceMap.push(
            { key: "authenticate", fct: this.authenticate },
            { key: "logout", fct: this.logout },
            { key: "clearSession", fct: this.clearSession },
            { key: "addUser", fct: this.addUser },
            { key: "updateUser", fct: this.updateUser },
            { key: "getCustomers", fct: this.getCustomers },
            { key: "getByGroup", fct: this.getByGroup },
            { key: "editUser", fct: this.editUser },
            { key: "editCompany", fct: this.editCompany },
            { key: "getRoles", fct: this.getRoles },
            {
                key: "getConnectedUserId",
                fct: this.getConnectedUserId,
            },
            { key: "getUserDetails", fct: this.getUserDetails },
            {
                key: "getConnectedUser",
                fct: this.getConnectedUser,
            },
            { key: "refreshSession", fct: this.refreshSession },
            { key: "sendChangePassword", fct: this.sendChangePassword },
            { key: "changePassword", fct: this.changePassword },
            { key: "setUserStatus", fct: this.setUserStatus },
            { key: "sendInvitationMail", fct: this.sendInvitationMail },
            { key: "resetPassword", fct: this.resetPassword },
            { key: "getUserStats", fct: this.getUserStats },
            { key: "getAdminStats", fct: this.getAdminStats },
            { key: "forgotten", fct: this.sendForgottenPwd },
            { key: "deleteAccount", fct: this.deleteAccount },
            { key: "activateAccount", fct: this.activateAccount },
            { key: "deleteAuthLink", fct: this.deleteAuthLink },
            { key: "getPreferences", fct: this.getPreferences },
            { key: "checkHasEntity", fct: this.checkHasEntity },
            { key: "authenticateAs", fct: this.authenticateAs },
            { key: "getUsersByGroupOwner", fct: this.getUsersByGroupOwner }
        )
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): UserService {
        if (!this.instance) {
            this.instance = new UserService()
        }
        return this.instance
    }

    /**
     * authenticate lance API REST de login afin de pouvoir se connecter
     * @param login l'email fourni par l'user
     * @param password le password fourni par l'user
     */
    public async authenticate(
        login: string,
        password: string
    ): Promise<{ accessToken: string; refreshToken?: string } | undefined> {
        try {
            const user = { login, password }
            const resToken: {
                err: boolean
                data: { accessToken: string; refreshToken?: string } | undefined
                statusCode: number
            } = await UserApi.login(user)

            // sauvegarde des token dans le local storage
            if (resToken?.data?.accessToken && resToken?.data?.refreshToken) {
                // stockage des token dans le LocalStoage
                await StoreService.getInstance().persistant.set(
                    "token",
                    resToken.data.accessToken
                )
                await StoreService.getInstance().persistant.set(
                    "refreshToken",
                    resToken.data.refreshToken
                )
                // shared token for cross-app SSO (dev: localStorage, prod: domain cookie set server-side)
                localStorage.setItem(
                    "cileamoov_access_token",
                    resToken.data.accessToken
                )
            }
            NavigationService.getInstance().handleRedir()
            return resToken.data
        } catch (err: any) {
            if (err?.response?.status === 400) {
                UIService.getInstance().showErrorToast(
                    i18next.t("Account_activation"),
                    "UserService"
                )
            } else if (err?.response?.status === 420) {
                UIService.getInstance().showErrorToast(
                    i18next.t("Account_login"),
                    "UserService"
                )
            } else if (err?.response?.status === 404) {
                UIService.getInstance().showErrorToast(
                    i18next.t("Account_not_found"),
                    "UserService"
                )
            }
            return undefined
        }
    }
    /**
     * authenticate lance API REST de login afin de pouvoir se connecter
     * @param login l'email fourni par l'user
     * @param password le password fourni par l'user
     */
    public async authenticateAs(
        login: string,
        password: string,
        userId: string
    ): Promise<{ accessToken: string; refreshToken?: string } | undefined> {
        try {
            const user = { login, password }
            const resToken: {
                err: boolean
                data: { accessToken: string; refreshToken?: string } | undefined
                statusCode: number
            } = await UserApi.loginAs(user, userId)

            // sauvegarde des token dans le local storage
            if (resToken?.data?.accessToken && resToken?.data?.refreshToken) {
                const store = StoreService.getInstance()
                if (!login && !password) {
                    // reset uniquement l'état mémoire, on garde les nouveaux tokens
                    // pour éviter les 401 transitoires pendant le switch de session.
                    store.clearSessionData()
                    NavigationService.getInstance().clearStack()
                    this.navParam.clear()
                }

                // stockage des token dans le LocalStorage
                await store.persistant.set("token", resToken.data.accessToken)
                await store.persistant.set(
                    "refreshToken",
                    resToken.data.refreshToken
                )
            }
            NavigationService.getInstance().refreshPage()
            return resToken.data
        } catch (err: any) {
            if (err?.response?.status === 420) {
                UIService.getInstance().showErrorToast(
                    i18next.t("Account_login"),
                    "UserService"
                )
            } else if (err?.response?.status === 404) {
                UIService.getInstance().showErrorToast(
                    i18next.t("Account_not_found"),
                    "UserService"
                )
            }
            return undefined
        }
    }

    /**
     * logout permet de se déconnecter en supprimant la session en cours
     */
    public async logout(): Promise<any> {
        const store = StoreService.getInstance()
        await store.persistant.delete("token")
        await store.persistant.delete("refreshToken")
        store.clearSessionData()
        NavigationService.getInstance().clearStack()
        this.navParam.clear()
        return Promise.resolve("logged out")
    }

    /**
     * clearSession permet de vider la session en cours
     */
    public async clearSession(): Promise<any> {
        const store = StoreService.getInstance()
        await store.persistant.delete("token")
        await store.persistant.delete("refreshToken")
        store.clearSessionData()
        NavigationService.getInstance().clearStack()
        return Promise.resolve("clear session")
    }
    /**
     * refreshSession refresh la session en cours en retournant un nouveau token
     */
    public async refreshSession(): Promise<any> {
        const store = StoreService.getInstance()
        const refreshToken = await StoreService.getInstance().persistant.get(
            "refreshToken"
        )
        if (refreshToken) {
            const newToken = await UserApi.refreshSession(refreshToken)
            // maj des token
            if (newToken?.accessToken && newToken?.refreshToken) {
                await store.persistant.set("token", newToken.accessToken)
                await store.persistant.set(
                    "refreshToken",
                    newToken.refreshToken
                )
            }
            return newToken
        }
        throw Error("No token found")
    }

    /**
     * Crée un User
     * @param firstName
     * @param lastName
     * @param permissionLevel
     * @param group
     * @param address
     * @param zipCode
     * @param city
     * @param email
     * @param phone
     * @param mobile
     * @returns
     */
    public async addUser(
        firstName: string,
        lastName: string,
        email: string,
        permissionLevel: number,
        group?: string,
        address?: string,
        zipCode?: string,
        city?: string,
        phone?: string,
        mobile?: string,
        sendEmail?: boolean,
        lang?: string,
        customerCode?: string,
        createGroup?: boolean
    ): Promise<
        | { message: string; group?: string; id: string; code?: number }
        | undefined
    > {
        const user = {
            firstName,
            lastName,
            email,
            group,
            permissionLevel,
            phone,
            mobile,
            address,
            zipCode,
            city,
            status: "inactive",
            customerCode,
            displayCustomerCode: customerCode,
        }

        return UserApi.createUser(user, sendEmail, lang, createGroup)
    }

    /**
     * getConnectedUserId retourne l'id de user connecté
     */
    public async getConnectedUserId(): Promise<string> {
        const token = await StoreService.getInstance().persistant.get("token")
        if (token) {
            const decoded = jwt_decode(token) as any
            return decoded.userId
        }
        throw Error("No token found")
    }

    /**
     * getConnectedUserGroup retourne l'id du group de l'user
     */
    public async getConnectedUserGroup(): Promise<string | null> {
        const token = await StoreService.getInstance().persistant.get("token")
        if (token) {
            const decoded = jwt_decode(token) as any
            if (decoded.group) return decoded.group
            if (decoded.permissionLevel === 15) return "supergroup"
            return null
        }

        return null
    }

    /**
     * isAdminOrSuperAdmin retourne vrai si l'user connecté est admin ou superuser
     */
    public async isAdminOrSuperAdmin(): Promise<boolean> {
        const token = await StoreService.getInstance().persistant.get("token")
        if (token) {
            const decoded = jwt_decode(token) as any
            if (decoded?.permissionLevel > 9) return true
            return false
        }
        throw Error("No token found")
    }

    /**
     * retourne l'objet user de l'utilisateur en session
     * ou null la session n'est pas disponible
     */
    public async getConnectedUser(): Promise<User | null> {
        const token = await StoreService.getInstance().persistant.get("token")
        if (token) {
            const decoded = jwt_decode(token) as any
            return decoded
        }
        return null
    }

    /**
     * Fourni la liste des users d'un group
     * @param groupId Nombre de clients
     * @param pl permission level
     */
    public async getByGroup(groupId: string, pl?: number): Promise<any> {
        const users = await UserApi.getByGroup(groupId, pl)
        return users || []
    }

    /**
     * Fourni la liste des clients du user connecté
     * Ou du group passé en param
     * @param limit Nombre de clients
     * @param page nombre de page
     * @param orderBy l'element de tri
     * @param sort le tri desc ou asc
     */
    public async getCustomers(
        groupId?: string,
        limit?: number,
        page?: number,
        orderBy?: string,
        sort?: string,
        q?: string
    ): Promise<any> {
        const user = await UserService.getInstance().getConnectedUser()
        let grpId = groupId as string
        if (!groupId) {
            grpId =
                user?.permissionLevel === 15
                    ? "supergroup"
                    : (user?.group as string)
        }

        if (grpId) {
            const customers = await UserApi.getUsers(
                grpId,
                false,
                limit,
                page,
                orderBy,
                sort,
                q
            )
            return customers
        }
        return []
    }

    /**
     * supprimer un compte utilisateur PL<11
     * @param userId l'id de user
     */
    public async deleteAccount(userId: string): Promise<any> {
        const deletetUser = await UserApi.deleteUser(userId)
        if (deletetUser) return deletetUser
        return null
    }

    /**
     * getRoles retourne tous les roles (selon les permissionLevel) disponibles en fonction
     * de l'user connecté
     */
    public getRoles(): Promise<any> {
        const roles = [
            { displayValue: "Super Utilisateur", value: 15 },
            { displayValue: "Administrateur", value: 10 },
            { displayValue: "Utilisateur", value: 1 },
        ]

        return Promise.resolve(roles)
    }

    public getDisplayValue(name: string, value: any): string {
        let displayValue = value
        if (name === "inputRole")
            switch (value) {
                case 15:
                    displayValue = "Super utilisateur"
                    break
                case 10:
                    displayValue = "Admin"
                    break
                case 1:
                    displayValue = "Utilisateur"
            }
        return displayValue
    }

    /**
     * getUserDetails appel l'API REST de getUserDetails afin de pouvoir récupérer
     * le user en fonction de son Id
     */
    public async getUserDetails(
        userId: string,
        link_auth?: string
    ): Promise<any> {
        const user = await UserApi.getUserDetails(userId, link_auth)
        return user
    }

    public async updateUser(
        userId: string,
        payload: Record<string, unknown>
    ): Promise<any> {
        return UserApi.updateUser(userId, payload)
    }

    /**
     * getUsersStats appel l'API REST de getActiveUsers et getActiveCustomers afin de regrouper
     * le résultat obtenu dans un tableau
     */
    public async getUserStats(group?: string): Promise<any> {
        const result = []
        const stats = await UserApi.getUserStats(group)
        if (stats && stats.userStats && stats.userStats?.length) {
            // push actif
            result.push({
                name: "Actif",
                value: stats.userStats[0].activeUser,
            })
            // push inactif
            result.push({
                name: "Non actif",
                value: stats.userStats[0].inactiveUser,
            })
        }
        return result
    }

    public async getAdminStats(): Promise<any> {
        const result = []
        const stats = await UserApi.getAdminStats()
        if (stats && stats.adminStats && stats.adminStats?.length) {
            // push actif
            result.push({
                name: "Actif",
                value: stats.adminStats[0].activeAdmin,
            })
            // push inactif
            result.push({
                name: "Non actif",
                value: stats.adminStats[0].inactiveAdmin,
            })
        }
        return result
    }

    /**
     * editUser lance API REST de update afin de pouvoir modifier
     * l'user courant
     * @param id l'id
     * @param firstName le nom
     * @param lastName le prénom
     * @param profilePicture id de la ged ou la photo de profile est stocké
     * @param email email user
     * @param phone numéro de téléphone
     * @param mobile numéro de mobile
     * @param address l'adresse
     * @param secondAddress l'adresse complémentaire
     * @param zipCode code postal
     * @param city la ville
     * @param corporateName optionel si  l'user est une entrepreprise
     * @param group group de user
     * @param customField champ libre (ex. SCG pour demat)
     */
    public async editUser(
        id: string,
        webId: string,
        firstName: string,
        lastName: string,
        profilePicture: string,
        email: string,
        phone: string,
        mobile: string,
        address: string,
        secondAddress: string,
        zipCode: string,
        city: string,
        corporateName?: string,
        group?: string,
        customField?: string
    ): Promise<any> {
        const user = {
            webId,
            firstName,
            lastName,
            profilePicture,
            email,
            phone,
            mobile,
            address,
            secondAddress,
            zipCode,
            city,
            corporateName,
            group,
            customField,
        }
        return UserApi.updateUser(id, user)
    }

    /**
     * setUserStatus met à jour le status de l'utilisateur
     * @param status status active / inactive
     */
    public async setUserStatus(id: string, status: string): Promise<any> {
        const user = {
            status,
        }
        return UserApi.updateUser(id, user)
    }

    /**
     * activateAccount activer le compte d'un user pour la premiére fois
     * @param link le lien d'invitation
     */
    public async activateAccount(link: string): Promise<any> {
        return UserApi.activateAccount(link)
    }

    /**
     * supprimer le lien temporarire utilisé par un user
     * @param link_auth le lien d'authentification de user
     */
    // TODO solution temporaire en attendant le fix de routing
    public async deleteAuthLink(link_auth: string): Promise<any> {
        const link = UserApi.deleteAuthLink(link_auth)
        this.navParam.clear()
        return link
    }

    /**
     * editCompany lance API REST de update afin de pouvoir modifier
     * l'entreprise courante
     * @param corporateName le nom de l'entreprise
     * @param siret SIRET de l'ESE
     * @param salesTaxNumber numéro de tax de l'ESE
     * @param email email user
     * @param phone numéro de téléphone
     * @param mobile numéro de mobile
     * @param address l'adresse
     * @param zipCode code postal
     * @param city la ville
     * @param customField champ libre (ex. SCG pour demat)
     */
    public async editCompany(
        webId: string,
        corporateName: string,
        siret: string,
        profilePicture: string,
        email: string,
        phone: string,
        mobile: string,
        address: string,
        zipCode: string,
        city: string,
        customField: string
    ): Promise<any> {
        const params = NavigationService.getInstance().getParams()
        if (params) {
            const user = {
                webId,
                corporateName,
                siret,
                profilePicture,
                email,
                phone,
                mobile,
                address,
                zipCode,
                city,
                customField,
            }
            const updatedUser = await UserApi.updateUser(params.id, user)
            return updatedUser
        }
        return undefined
    }

    public async changePassword(
        oldPassword: string,
        password: string,
        passwordConfirmation: string,
        link: string
    ): Promise<any> {
        const pass = {
            oldPassword,
            password,
            passwordConfirmation,
        }

        const updatedUser = await UserApi.changePassword(pass, link)
        return updatedUser
    }

    public async resetPassword(
        password: string,
        passwordConfirmation: string,
        link: string
    ): Promise<any> {
        const pass = {
            password: password.trim(),
            passwordConfirmation: passwordConfirmation.trim(),
        }
        const updatedUser = await UserApi.changePassword(pass, link)
        return updatedUser
    }

    /**
     * sendInvitationMail envoie un mail avec un lien d'invitation
     * @param to dest email
     * @param from expediteur email
     */
    public async sendInvitationMail(to: string, from: string): Promise<void> {
        const langFromPersistence =
            await StoreService.getInstance().persistant.get("lang")
        const send = await UserApi.sendInvitation(
            to,
            from,
            langFromPersistence || "fr"
        )
        if (send)
            return UIService.getInstance().showConfirmationToast(
                "Un mail d'invitation a été envoyé",
                "userService"
            )
        return UIService.getInstance().showErrorToast(
            "Un probléme est survenu lors de l'envoi de mail d'invitation",
            "userService"
        )
    }

    /**
     * Envoie un lien pour changer son mot de passe
     * @param to dest email
     * @param from expediteur email
     */
    public async sendChangePassword(to: string, from: string): Promise<void> {
        const langFromPersistence =
            await StoreService.getInstance().persistant.get("lang")
        const send = await UserApi.sendChangePassword(
            to,
            from,
            langFromPersistence || "fr"
        )
        if (send)
            return UIService.getInstance().showConfirmationToast(
                "Un mail pour changer le mot de passe a été envoyé",
                "userService"
            )
        return UIService.getInstance().showErrorToast(
            "Un probléme est survenu lors de l'envoi de mail de réinitialisation",
            "userService"
        )
    }

    /**
     * Envoie un email en cas d'oublie de mot de passe
     * @param to dest email
     */
    public async sendForgottenPwd(
        to: string,
        showToast: boolean = true
    ): Promise<void> {
        const langFromPersistence =
            await StoreService.getInstance().persistant.get("lang")
        const send = await UserApi.sendForgottenPwd(
            to,
            langFromPersistence || "fr"
        )
        if (send && showToast)
            return UIService.getInstance().showConfirmationToast(
                i18next.t("resetEmailSuccess"),
                "userService"
            )
        if (showToast) {
            return UIService.getInstance().showErrorToast(
                i18next.t("errorSendEmail"),
                "userService"
            )
        }
        return
    }

    /**
     * updatePref lance API REST de update user preferences afin de pouvoir modifier
     * les preferences de user
     * @param pref les preferences
     * @param type le type de setting qu'on veut modifier (attr de l'obj setting)
     */
    public async updatePreferences(
        userId: string,
        pref: unknown,
        type: string
    ): Promise<any> {
        if (userId) {
            const params: any = {}
            params[type] = pref
            return UserApi.updatePreferences(userId, params, type)
        }
        return Promise.reject(Error("Pas d'entity trouvée"))
    }

    /**
     * Récupère une preferences de user en fonction de sa clef
     * @param userId id user
     * @param type clef de la pref qu'on veut récupérer
     * @returns unknow
     */
    public async getPreferences(userId: string, type: string) {
        if (userId) {
            return UserApi.getPreferences(userId, type)
        }
        return Promise.reject(Error("Pas de user trouvée"))
    }
    /**
     * checkHasEntity
     * vérifie si user a un compte superviseur
     * retourne les comptes superviseurs ainsi que les autres comptes
     * @param login login user webId/email
     * @param password mot de passe user
     */
    public async checkHasEntity(login: string, password: string): Promise<any> {
        const check = await UserApi.checkHasEntity(login, password)
        if (check?.supervisors && check.supervisors.length > 0) {
            StoreService.getInstance().local.set(
                "supervisor",
                check.supervisors[0]
            )
        }
        return check
    }

    public async getUsersByGroupOwner(ownerId: string) {
        return UserApi.getUsersByGroupOwner(ownerId)
    }
}

export default UserService
